import {
    AnyImageOpResult,
    AnyImageOpResultNode,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodePrepareMask} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/prepare-mask/node"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {ImageOpNodeBlit} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/node"
import {ImageOpNodeDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/node"
import {BrushTool} from "@app/textures/texture-editor/operator-stack/operators/shared/toolbox/brush-tool"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {OperatorExecutionMode} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {BakedMaskCache} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/baked-mask-cache"
import {Box2} from "@cm/math"

export type PrepareAndBakeMinimalMaskArgs = {
    sharedImageOpNodeBag: ImageOpNodeBag
    sourceImage: AnyImageOpResultNode
    mode: OperatorExecutionMode
    dataObjectId: string | undefined
    maskRegion: TextureEditNodes.Region | undefined
    brushToolboxItem: BrushTool
    maskBakeCache: BakedMaskCache
    onBaked: (dataObjectId: string, region: TextureEditNodes.Region) => void
}

/**
 * Shared by OperatorLayerAndMask and OperatorHighpass: loads a single, continuously-painted mask that is stored as only
 * the minimal bounding box ever painted on (generalizing clone stamp's per-stroke maskRegion, see
 * operator-clone-stamp.ts, to one running region), reconstructs it to full canvas size for painting and for the
 * consuming node (which still expects a full-size mask, unchanged), and re-bakes/uploads only the (possibly grown)
 * union of the previously stored region and any new strokes.
 *
 * `maskRegion === undefined` means either a legacy full-canvas mask or no mask baked yet, and is handled without any
 * cropping/repositioning, so old projects keep loading unchanged.
 *
 * Bake/upload lifecycle is owned by `maskBakeCache` (a `BakedMaskCache` the caller persists across calls): in HAL/
 * preview mode the live-painted result is captured there as a side effect (regardless of whether it's currently
 * displayed); in imgproc/final mode, if there are uncommitted edits, the cached result is uploaded and the returned
 * id is persisted back via `onBaked`. When nothing has changed since the last bake, neither path runs at all -- the
 * already-loaded mask (by `dataObjectId`) is used directly.
 */
export async function prepareAndBakeMinimalMask(args: PrepareAndBakeMinimalMaskArgs): Promise<ValueOrNode<AnyImageOpResult>> {
    const {sharedImageOpNodeBag: bag, sourceImage, mode, dataObjectId, maskRegion, brushToolboxItem, maskBakeCache, onBaked} = args

    const sourceImageDescriptor = bag.getLambda("getSourceImageDescriptor", {sourceImage}, ({sourceImage}) => ({
        ...sourceImage.descriptor,
        channels: 1,
    }))

    const loadedMask = bag.getNode("prepareMask", ImageOpNodePrepareMask, {
        dataObjectId,
        descriptor: maskRegion ? {width: maskRegion.width, height: maskRegion.height, channels: 1} : sourceImageDescriptor,
    })

    // reposition the (possibly small, natively-sized) loaded mask within the full canvas before handing it to the brush
    // tool, so painting can extend an already-minimal mask exactly like painting on a legacy full-canvas one
    let reconstructedMask: ValueOrNode<AnyImageOpResult> = loadedMask
    if (maskRegion) {
        const emptyCanvas = bag.getNode("emptyMaskCanvas", ImageOpNodeCreateImage, {
            descriptor: sourceImageDescriptor,
            data: 0,
        })
        reconstructedMask = bag.getNode("reconstructMask", ImageOpNodeBlit, {
            backgroundImage: emptyCanvas,
            foregroundImage: loadedMask,
            position: {x: maskRegion.x, y: maskRegion.y},
        })
    }

    if (!brushToolboxItem.hasEdits && !brushToolboxItem.isDrawing) {
        // nothing painted since the last bake, and no stroke currently in progress -- the loaded/reconstructed
        // mask already is exactly what a consuming node expects (or, if never baked, an empty canvas); no
        // bake/upload needed at all. hasEdits alone isn't enough here: it only tracks committed strokes, not
        // the one currently being dragged (see BrushTool.isDrawing), which would otherwise not render live.
        return reconstructedMask
    }

    const brushToolNodes = brushToolboxItem.getImageOpNodes({sourceImage: reconstructedMask})
    const paintedMask = brushToolNodes.resultImage
    const newStrokesBoundingBox = brushToolNodes.boundingBox

    const uploadBoundingBox = bag.getLambda(
        "getUploadBoundingBox",
        {newStrokesBoundingBox, sourceImageDescriptor},
        ({newStrokesBoundingBox, sourceImageDescriptor}) => {
            // preserve everything the previously-baked mask covered: an existing minimal region as-is, or the
            // whole canvas for a legacy full-size mask being re-baked for the first time (we can't know which
            // of its pixels are actually painted without inspecting content, so it only starts shrinking from
            // the next edit onward)
            const existingRegion = maskRegion ?? (dataObjectId ? Box2.fromSize(sourceImageDescriptor) : undefined)
            const base = existingRegion ? Box2.fromBox2Like(existingRegion) : Box2.fromBox2Like(newStrokesBoundingBox)
            return base.expandByBoxInPlace(newStrokesBoundingBox).expandToIntegersInPlace().intersectInPlace(Box2.fromSize(sourceImageDescriptor))
        },
    )

    if (mode !== "final") {
        // preview: keep the bake cache warm regardless of whether this mask is currently displayed, and show
        // the live-painted result as-is (never cropped) -- this is a pure pass-through, no upload happens here
        return maskBakeCache.captureViaGraph(bag, "captureMask", paintedMask, uploadBoundingBox)
    }

    const {dataObjectId: bakedDataObjectId, region} = await maskBakeCache.getDataObjectId()
    onBaked(bakedDataObjectId, {type: "region", ...region})
    brushToolboxItem.removeAllStrokes()

    // reconstruct the (possibly cropped) baked mask back to full canvas size -- same contract as before
    const bakedMaskImage = bag.getNode("loadBakedMask", ImageOpNodeDataObject, {
        dataObjectId: bakedDataObjectId,
        downloadFormat: "exr",
        channels: 1,
    })
    const finalEmptyCanvas = bag.getNode("finalMaskCanvas", ImageOpNodeCreateImage, {
        descriptor: sourceImageDescriptor,
        data: 0,
    })
    return bag.getNode("finalMask", ImageOpNodeBlit, {
        backgroundImage: finalEmptyCanvas,
        foregroundImage: bakedMaskImage,
        position: {x: region.x, y: region.y},
    })
}
